import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useSnackbar } from "notistack";
import CustomTextField from "components/Form/CustomTextField";
import WeeklySchedule from "../WeeklySchedule";
import EmployeeSearchBar from "../EmployeeSearchBar";
import ShiftPositionModal from "./ShiftPositionModal";
import ShiftPositions from "./ShiftPositions";

// Separate StaffTable component to prevent re-renders
const StaffTable = React.memo(
  ({
    staffList,
    selectedStaff,
    onStaffClick,
    onSelectNone,
    onSelectAll,
    type,
    positions,
    onPositionChange,
    onRateChange,
    theme,
    loading = false, // add loading prop, default false
  }) => {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          borderTop: "none",
          height: 300,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              type === "assigned" && positions.length > 0 ? "2fr 1.5fr 1fr 1fr" : "2fr 1fr 1fr",
            backgroundColor: "transparent",
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 600,
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
          }}
        >
          {type === "assigned" ? (
            <>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textAlign: "left",
                  paddingLeft: 2,
                  paddingY: 1,
                }}
              >
                Name
              </Typography>
              {positions.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    paddingY: 1,
                  }}
                >
                  Position
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                  paddingY: 1,
                }}
              >
                Pay Rate
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                  paddingY: 1,
                }}
              >
                Bill Rate
              </Typography>
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textAlign: "left",
                paddingLeft: 2,
                paddingY: 1,
              }}
            >
              Name
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          {loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: theme.palette.text.secondary,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          {!loading &&
            staffList.length > 0 &&
            staffList.map((staff) => (
              <Box
                key={staff.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    type === "assigned" &&
                    (e.target.tagName === "INPUT" || e.target.closest(".MuiTextField-root"))
                  ) {
                    return;
                  }
                  onStaffClick(staff, type);
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    type === "assigned" && positions.length > 0
                      ? "2fr 1.5fr 1fr 1fr"
                      : "2fr 1fr 1fr",
                  p: 1,
                  gap: 2,
                  cursor: "pointer",
                  backgroundColor: selectedStaff.includes(staff.id)
                    ? "#e3f2fd" // light blue
                    : "transparent",
                  "&:hover": {
                    backgroundColor: selectedStaff.includes(staff.id)
                      ? "#e3f2fd"
                      : theme.palette.action.hover,
                  },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  alignItems: "center",
                  userSelect: "none",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {staff.name}
                </Typography>

                {type === "assigned" && (
                  <>
                    {positions.length > 0 && (
                      <Autocomplete
                        size="small"
                        options={positions.map((pos) => pos.positionName)}
                        value={staff.assignedPosition || ""}
                        onChange={(e, newValue) => {
                          e.stopPropagation();
                          onPositionChange(staff.id, newValue);
                        }}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            placeholder="Select position"
                            onFocus={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: 32,
                                fontSize: "0.875rem",
                              },
                              "& input": {
                                padding: "6px 8px",
                              },
                            }}
                          />
                        )}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 32,
                          },
                        }}
                      />
                    )}
                    <CustomTextField
                      size="small"
                      type="number"
                      value={staff.payRate || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        onRateChange(staff.id, "payRate", e.target.value);
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: "$",
                        inputProps: {
                          min: 0,
                          step: "0.01",
                          inputMode: "decimal",
                        },
                      }}
                      sx={{
                        mx: 0.5,
                        "& .MuiOutlinedInput-root": {
                          height: 32,
                          fontSize: "0.875rem",
                        },
                        "& input": {
                          textAlign: "center",
                          padding: "6px 8px",
                        },
                      }}
                    />
                    <CustomTextField
                      size="small"
                      type="number"
                      value={staff.billRate || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        onRateChange(staff.id, "billRate", e.target.value);
                      }}
                      onFocus={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: "$",
                        inputProps: {
                          min: 0,
                          step: "0.01",
                          inputMode: "decimal",
                        },
                      }}
                      sx={{
                        mx: 0.5,
                        "& .MuiOutlinedInput-root": {
                          height: 32,
                          fontSize: "0.875rem",
                        },
                        "& input": {
                          textAlign: "center",
                          padding: "6px 8px",
                        },
                      }}
                    />
                  </>
                )}
              </Box>
            ))}
          {!loading && staffList.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: theme.palette.text.secondary,
              }}
            >
              <Typography variant="body2">
                {type === "available" ? "No available employees" : "No assigned employees"}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => onSelectNone(type)}
            sx={{
              flex: 1,
              py: 1,
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.grey[200],
              borderRadius: 0,
              fontSize: "0.75rem",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: theme.palette.grey[300],
              },
            }}
          >
            NONE
          </Button>
          <Button
            onClick={() => onSelectAll(type)}
            variant="contained"
            sx={{
              flex: 1,
              py: 1,
              borderRadius: 0,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            SELECT ALL
          </Button>
        </Box>
      </Box>
    );
  }
);

const ShiftSettingsModal = ({
  open,
  onClose,
  shiftData,
  onSave,
  availableStaff = [],
  assignedStaff = [],
  existingShifts = [],
  loading = false, // add loading prop, default false
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAvailableStaff, setSelectedAvailableStaff] = useState([]);
  const [selectedAssignedStaff, setSelectedAssignedStaff] = useState([]);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [isValidScheduleShifts, setIsValidScheduleShifts] = useState(true);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [shiftPositionModalOpen, setShiftPositionModalOpen] = useState(false);
  const [positionIndex, setPositionIndex] = useState(null);

  // Add ref for search input
  const searchInputRef = useRef(null);

  // Add validation state
  const [errors, setErrors] = useState({
    shiftName: "",
    shiftStartDate: "",
    shiftEndDate: "",
  });

  // Use useMemo to prevent unnecessary recalculations
  const initialFormData = useMemo(
    () => ({
      shiftName: "",
      shiftStartDate: null,
      shiftEndDate: null,
      payRate: "",
      billRate: "",
      positions: [],
      defaultSchedule: {
        monday: { from: null, to: null },
        tuesday: { from: moment("13:00", "HH:mm"), to: moment("14:00", "HH:mm") },
        wednesday: { from: null, to: null },
        thursday: { from: null, to: null },
        friday: { from: null, to: null },
        saturday: { from: null, to: null },
        sunday: { from: null, to: null },
      },
      assignedStaff: [],
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormData);

  // Ref to track if the form has been initialized to prevent re-initialization on re-renders
  const isInitialized = useRef(false);
  const saveTimeoutRef = useRef(null);

  const debouncedSave = useCallback(
    (data) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        if (open && isInitialized.current) {
          try {
            sessionStorage.setItem("shiftSettingsFormData", JSON.stringify(data));
          } catch (error) {
            console.warn("Failed to save form data to sessionStorage:", error);
          }
        }
      }, 500); // Reduced from 2000ms to 500ms for better UX
    },
    [open]
  );

  // Persist form data in sessionStorage to prevent reset on tab change
  useEffect(() => {
    if (open) {
      if (!isInitialized.current) {
        const savedFormData = sessionStorage.getItem("shiftSettingsFormData");
        if (savedFormData) {
          try {
            const parsed = JSON.parse(savedFormData, (key, value) => {
              // Reviver function to convert ISO strings back to moment objects
              if (
                value &&
                typeof value === "string" &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)
              ) {
                return moment(value);
              }
              return value;
            });
            setFormData(parsed);
            // Clear errors when loading saved data
            setErrors({
              shiftName: "",
              shiftStartDate: "",
              shiftEndDate: "",
            });
          } catch (error) {
            console.error("Error parsing saved form data:", error);
            // If parsing fails, initialize from shiftData
            if (shiftData) {
              const newFormData = {
                shiftName: shiftData.shiftName || "",
                shiftStartDate: shiftData.shiftStartDate ? moment(shiftData.shiftStartDate) : null,
                shiftEndDate: shiftData.shiftEndDate ? moment(shiftData.shiftEndDate) : null,
                payRate: shiftData.payRate || "",
                billRate: shiftData.billRate || "",
                positions: shiftData.positions || [],
                schedule:
                  shiftData.schedule ||
                  shiftData.defaultSchedule ||
                  initialFormData.defaultSchedule,
                defaultSchedule:
                  shiftData.schedule ||
                  shiftData.defaultSchedule ||
                  initialFormData.defaultSchedule,
                assignedStaff: assignedStaff || shiftData.assignedStaff || [],
                shiftRoster: shiftData.shiftRoster || [],
                slug: shiftData.slug || "",
              };
              setFormData(newFormData);
              // Clear errors when loading shift data
              setErrors({
                shiftName: "",
                shiftStartDate: "",
                shiftEndDate: "",
              });
            }
          }
        } else if (shiftData) {
          const newFormData = {
            shiftName: shiftData.shiftName || "",
            shiftStartDate: shiftData.shiftStartDate ? moment(shiftData.shiftStartDate) : null,
            shiftEndDate: shiftData.shiftEndDate ? moment(shiftData.shiftEndDate) : null,
            payRate: shiftData.payRate || "",
            billRate: shiftData.billRate || "",
            positions: shiftData.positions || [],
            schedule:
              shiftData.schedule || shiftData.defaultSchedule || initialFormData.defaultSchedule,
            defaultSchedule:
              shiftData.schedule || shiftData.defaultSchedule || initialFormData.defaultSchedule,
            assignedStaff: assignedStaff || shiftData.assignedStaff || [],
            shiftRoster: shiftData.shiftRoster || [],
            slug: shiftData.slug || "",
          };
          setFormData(newFormData);
          // Clear errors when loading shift data
          setErrors({
            shiftName: "",
            shiftStartDate: "",
            shiftEndDate: "",
          });
        }
        isInitialized.current = true;
      }
    } else {
      // Reset initialization state and clear storage when modal is closed
      isInitialized.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      sessionStorage.removeItem("shiftSettingsFormData");
    }
  }, [open, shiftData, initialFormData, assignedStaff]);

  // Debounced save form data to sessionStorage whenever it changes
  useEffect(() => {
    if (open && isInitialized.current) {
      debouncedSave(formData);
    }
  }, [formData, open, debouncedSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsValidScheduleShifts(true);
  }, [shiftData]);

  const dayLabels = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const updateFormField = useCallback((field, value) => {
    setFormData((prev) => {
      // Only update if the value actually changed
      if (prev[field] === value) {
        return prev;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  // Separate function to clear errors
  const clearFieldError = useCallback((field) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const handleTimeChange = useCallback((day, field, newValue) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: newValue,
        },
      },
    }));
  }, []);

  // Memoize filtered staff to prevent unnecessary recalculations
  const filteredAvailableStaff = useMemo(() => {
    const assignedIds = formData.assignedStaff.map((s) => s.id);
    return availableStaff
      .filter((staff) => !assignedIds.includes(staff.id))
      .filter((staff) => staff?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      .sort((a, b) => {
        // Split names into first and last name for proper sorting
        const aName = a?.name || "";
        const bName = b?.name || "";

        const aParts = aName?.trim()?.split(" ");
        const bParts = bName?.trim()?.split(" ");

        const aFirstName = aParts?.[0] || "";
        const aLastName = aParts?.slice(1)?.join(" ") || "";
        const bFirstName = bParts?.[0] || "";
        const bLastName = bParts?.slice(1)?.join(" ") || "";

        // First compare by last name
        const lastNameComparison = aLastName?.localeCompare(bLastName);
        if (lastNameComparison !== 0) {
          return lastNameComparison;
        }

        // If last names are the same, compare by first name
        return aFirstName.localeCompare(bFirstName);
      });
  }, [availableStaff, formData.assignedStaff, searchTerm]);

  const handleMoveStaff = useCallback(
    (direction) => {
      if (direction === "right") {
        const newAssigned = [...formData.assignedStaff];
        selectedAvailableStaff.forEach((staffId) => {
          const staff = availableStaff.find((s) => s.id === staffId);
          if (staff && !newAssigned.find((a) => a.id === staffId)) {
            newAssigned.push(staff);
          }
        });
        setFormData((prev) => ({ ...prev, assignedStaff: newAssigned }));
        setSelectedAvailableStaff([]);
      } else {
        const newAssigned = formData.assignedStaff.filter(
          (staff) => !selectedAssignedStaff.includes(staff.id)
        );
        setFormData((prev) => ({ ...prev, assignedStaff: newAssigned }));
        setSelectedAssignedStaff([]);
      }
    },
    [formData.assignedStaff, selectedAvailableStaff, selectedAssignedStaff, availableStaff]
  );

  const handleSelectAll = useCallback(
    (type) => {
      if (type === "available") {
        setSelectedAvailableStaff(filteredAvailableStaff.map((s) => s.id));
      } else {
        setSelectedAssignedStaff(formData.assignedStaff.map((s) => s.id));
      }
    },
    [filteredAvailableStaff, formData.assignedStaff]
  );

  const handleSelectNone = useCallback((type) => {
    if (type === "available") {
      setSelectedAvailableStaff([]);
    } else {
      setSelectedAssignedStaff([]);
    }
  }, []);

  // Helper function to get position availability
  const getPositionAvailability = useCallback(
    (positionName) => {
      const position = formData.positions.find((pos) => pos.positionName === positionName);
      if (!position) return { total: 0, assigned: 0, available: 0 };

      const total = parseInt(position.numberPositions, 10) || 0;
      const assigned = formData.assignedStaff.filter(
        (staff) => staff.assignedPosition === positionName
      ).length;
      const available = total - assigned;

      return { total, assigned, available };
    },
    [formData.positions, formData.assignedStaff]
  );

  // Helper function to check if position assignment is allowed
  const canAssignToPosition = useCallback(
    (positionName) => {
      const { available } = getPositionAvailability(positionName);
      return available > 0;
    },
    [getPositionAvailability]
  );

  // Position handlers
  const addNewPosition = useCallback(() => {
    setShiftPositionModalOpen(true);
  }, []);

  const positionHandler = useCallback((row, isOpenPositionModal, posIdx) => {
    setCurrentPosition(row);
    setShiftPositionModalOpen(isOpenPositionModal);
    setPositionIndex(posIdx);
  }, []);

  const removePosition = useCallback((idx) => {
    setFormData((prev) => {
      // Get the position name that's being deleted
      const deletedPositionName = prev.positions[idx]?.positionName;

      // Remove the position from positions array
      const updatedPositions = prev.positions.filter((_, i) => i !== idx);

      // Clear the assignedPosition for any staff that was assigned to the deleted position
      const updatedAssignedStaff = prev.assignedStaff.map((staff) => ({
        ...staff,
        assignedPosition:
          staff.assignedPosition === deletedPositionName ? "" : staff.assignedPosition,
      }));

      return {
        ...prev,
        positions: updatedPositions,
        assignedStaff: updatedAssignedStaff,
      };
    });
  }, []);

  const handleApplyRatesToAll = useCallback(() => {
    setFormData((prev) => {
      const updatedAssignedStaff = prev.assignedStaff.map((staff) => ({
        ...staff,
        payRate: prev.payRate || staff.payRate,
        billRate: prev.billRate || staff.billRate,
      }));

      return {
        ...prev,
        assignedStaff: updatedAssignedStaff,
      };
    });
  }, []);

  // Validation function
  const validateForm = useCallback(() => {
    const newErrors = {
      shiftName: "",
      shiftStartDate: "",
      shiftEndDate: "",
    };

    if (!formData.shiftName.trim()) {
      newErrors.shiftName = "Shift name is required";
    } else {
      // Check for duplicate shift names
      const isDuplicate = existingShifts.some((shift) => {
        // Don't compare with the current shift being edited
        const isCurrentShift = shiftData && shift.slug === shiftData.slug;
        return (
          !isCurrentShift &&
          shift.shiftName?.toLowerCase() === formData.shiftName.trim().toLowerCase()
        );
      });

      if (isDuplicate) {
        newErrors.shiftName = "Shift name must be unique";
      }
    }

    if (!formData.shiftStartDate) {
      newErrors.shiftStartDate = "Start date is required";
    }

    if (!formData.shiftEndDate) {
      newErrors.shiftEndDate = "End date is required";
    }

    // Check if end date is after start date
    if (
      formData.shiftStartDate &&
      formData.shiftEndDate &&
      formData.shiftEndDate.isBefore(formData.shiftStartDate)
    ) {
      newErrors.shiftEndDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  }, [
    formData.shiftName,
    formData.shiftStartDate,
    formData.shiftEndDate,
    existingShifts,
    shiftData,
  ]);

  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }
    if (onSave) {
      onSave(formData);
    }
  }, [validateForm, onSave, formData, onClose]);

  const formatTime = useCallback((time) => {
    return time ? time.format("hh:mm A") : "--:-- --";
  }, []);

  const handleStaffClick = useCallback(
    (staff, type) => {
      const currentTime = new Date().getTime();
      const isDoubleClick = currentTime - lastClickTime < 300;
      setLastClickTime(currentTime);

      if (isDoubleClick) {
        if (type === "available") {
          if (!formData.assignedStaff.some((a) => a.id === staff.id)) {
            setFormData((prev) => ({ ...prev, assignedStaff: [...prev.assignedStaff, staff] }));
            setSelectedAvailableStaff((prev) => prev.filter((id) => id !== staff.id));
          }
        } else if (type === "assigned") {
          setFormData((prev) => ({
            ...prev,
            assignedStaff: prev.assignedStaff.map((s) =>
              s.id === staff.id ? { ...s, payRate: (parseFloat(s.payRate) || 0) + 1 } : s
            ),
          }));
        }
        return;
      }

      // Handle single click
      if (type === "available") {
        setSelectedAvailableStaff((prev) =>
          selectedAvailableStaff.includes(staff.id)
            ? prev.filter((id) => id !== staff.id)
            : [...prev, staff.id]
        );
      } else {
        setSelectedAssignedStaff((prev) =>
          selectedAssignedStaff.includes(staff.id)
            ? prev.filter((id) => id !== staff.id)
            : [...prev, staff.id]
        );
      }
    },
    [lastClickTime, formData.assignedStaff, selectedAvailableStaff, selectedAssignedStaff]
  );

  // Create refs for stable access to current values
  const formDataRef = useRef();
  const selectedAvailableStaffRef = useRef();
  const selectedAssignedStaffRef = useRef();
  const lastClickTimeRef = useRef(0);

  // Update refs on every render
  useEffect(() => {
    formDataRef.current = formData;
    selectedAvailableStaffRef.current = selectedAvailableStaff;
    selectedAssignedStaffRef.current = selectedAssignedStaff;
  });

  // Truly stable callback handlers for the external StaffTable component
  const handlePositionChangeStable = useCallback(
    (staffId, newValue) => {
      // Check if the new position has availability (only if changing to a different position)
      const currentFormData = formDataRef.current;
      const staff = currentFormData.assignedStaff.find((s) => s.id === staffId);
      if (newValue && newValue !== staff?.assignedPosition) {
        // Get position availability
        const position = currentFormData.positions.find((pos) => pos.positionName === newValue);
        if (position) {
          const total = parseInt(position.numberPositions, 10) || 0;
          const assigned = currentFormData.assignedStaff.filter(
            (s) => s.assignedPosition === newValue
          ).length;

          if (assigned >= total) {
            enqueueSnackbar(
              `Cannot assign to ${newValue}. All ${total} positions are already filled (${assigned}/${total} assigned).`,
              { variant: "error" }
            );
            return;
          }

          // Populate pay rate and bill rate from position if they exist
          const updatedStaff = currentFormData.assignedStaff.map((s) => {
            if (s.id === staffId) {
              return {
                ...s,
                assignedPosition: newValue,
                payRate: position.payRate || s.payRate || "",
                billRate: position.billRate || s.billRate || "",
              };
            }
            return s;
          });
          setFormData((prev) => ({ ...prev, assignedStaff: updatedStaff }));
        } else {
          // If position not found, just update the position assignment
          const updatedStaff = currentFormData.assignedStaff.map((s) =>
            s.id === staffId ? { ...s, assignedPosition: newValue } : s
          );
          setFormData((prev) => ({ ...prev, assignedStaff: updatedStaff }));
        }
      } else {
        // If no position change or position is being cleared
        const updatedStaff = currentFormData.assignedStaff.map((s) =>
          s.id === staffId ? { ...s, assignedPosition: newValue } : s
        );
        setFormData((prev) => ({ ...prev, assignedStaff: updatedStaff }));
      }
    },
    [enqueueSnackbar]
  );

  const handleRateChangeStable = useCallback((staffId, field, value) => {
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        assignedStaff: prev.assignedStaff.map((s) =>
          s.id === staffId ? { ...s, [field]: value } : s
        ),
      }));
    }
  }, []);

  const handleStaffClickStable = useCallback((staff, type) => {
    const { id: staffId } = staff;
    const currentTime = new Date().getTime();
    const isDoubleClick = currentTime - lastClickTimeRef.current < 300;
    lastClickTimeRef.current = currentTime;
    setLastClickTime(currentTime);

    if (isDoubleClick) {
      if (type === "available") {
        const currentFormData = formDataRef.current;
        if (!currentFormData.assignedStaff.some((a) => a.id === staffId)) {
          setFormData((prev) => ({ ...prev, assignedStaff: [...prev.assignedStaff, staff] }));
          setSelectedAvailableStaff((prev) => prev.filter((id) => id !== staffId));
        }
      }
      return;
    }

    // Handle single click
    if (type === "available") {
      setSelectedAvailableStaff((prev) => {
        const { current } = selectedAvailableStaffRef;
        return current.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId];
      });
    } else {
      setSelectedAssignedStaff((prev) => {
        const { current } = selectedAssignedStaffRef;
        return current.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId];
      });
    }
  }, []);

  // Get dynamic title
  const getModalTitle = () => {
    if (!formData.shiftName.trim()) {
      return "Add Shift";
    }
    return `Shift Settings for: ${formData.shiftName}`;
  };

  // Get title color based on mode
  const getTitleColor = () => {
    if (!formData.shiftName.trim()) {
      return theme.palette.primary.main; // Blue for Add Shift
    }
    return theme.palette.success.main; // Green for editing existing shift
  };

  const isFormValid = useMemo(() => {
    return (
      formData.shiftName.trim() !== "" &&
      formData.shiftStartDate !== null &&
      formData.shiftEndDate !== null &&
      (!formData.shiftEndDate ||
        !formData.shiftStartDate ||
        !formData.shiftEndDate.isBefore(formData.shiftStartDate))
    );
  }, [formData.shiftName, formData.shiftStartDate, formData.shiftEndDate]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          width: "90vw",
          maxWidth: "1500px",
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {!formData.shiftName.trim() ? (
              "Add Shift"
            ) : (
              <>
                Shift Settings for:{" "}
                <span style={{ color: theme.palette.primary.main }}>{formData.shiftName}</span>
              </>
            )}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          <Box sx={{ position: "relative", width: "100%", paddingBottom: 3, ml: 0.5 }}>
            <EmployeeSearchBar />
          </Box>

          <Grid container sx={{ mb: 3 }}>
            <Grid item xs={5.5}>
              {/* Search box styled to look like table header */}
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  border: `1px solid ${theme.palette.divider}`,
                  borderBottom: "none",
                  borderRadius: "4px 4px 0 0",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: 1,
                  height: 45,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    minWidth: "fit-content",
                  }}
                >
                  Available Employees
                </Typography>
                <CustomTextField
                  size="small"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  inputRef={searchInputRef}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      height: 24,
                      fontSize: "0.75rem",
                    },
                    "& input": {
                      padding: "4px 6px",
                      width: 200,
                    },
                  }}
                />
              </Box>
              <StaffTable
                key="available"
                staffList={filteredAvailableStaff}
                selectedStaff={selectedAvailableStaff}
                onStaffClick={handleStaffClickStable}
                onSelectNone={handleSelectNone}
                onSelectAll={handleSelectAll}
                type="available"
                positions={[]}
                onPositionChange={handlePositionChangeStable}
                onRateChange={handleRateChangeStable}
                theme={theme}
                loading={loading} // pass loading
              />
            </Grid>

            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() => handleMoveStaff("left")}
                disabled={selectedAssignedStaff.length === 0}
                sx={{
                  minWidth: 50,
                  height: 50,
                  borderRadius: "50%",
                  backgroundColor: "#6c63ff",
                  "&:hover": {
                    backgroundColor: "#5a52d5",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.grey[300],
                  },
                }}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                variant="contained"
                onClick={() => handleMoveStaff("right")}
                disabled={selectedAvailableStaff.length === 0}
                sx={{
                  minWidth: 50,
                  height: 50,
                  borderRadius: "50%",
                  backgroundColor: "#6c63ff",
                  "&:hover": {
                    backgroundColor: "#5a52d5",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.grey[300],
                  },
                }}
              >
                <ArrowForwardIcon />
              </Button>
            </Grid>

            <Grid item xs={5.5}>
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  border: `1px solid ${theme.palette.divider}`,
                  borderBottom: "none",
                  borderRadius: "4px 4px 0 0",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: 45,
                  boxShadow: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    minWidth: "fit-content",
                  }}
                >
                  Assigned Employees
                </Typography>
              </Box>
              <StaffTable
                key={`assigned-${formData.positions.length}`}
                staffList={formData.assignedStaff?.sort((a, b) => {
                  const lastNameA = (a?.lastName || "").trim();
                  const lastNameB = (b?.lastName || "").trim();
                  const firstNameA = (a?.firstName || "").trim();
                  const firstNameB = (b?.firstName || "").trim();

                  if (lastNameA < lastNameB) return -1;
                  if (lastNameA > lastNameB) return 1;
                  if (firstNameA < firstNameB) return -1;
                  if (firstNameA > firstNameB) return 1;
                  return 0;
                })}
                selectedStaff={selectedAssignedStaff}
                onStaffClick={handleStaffClickStable}
                onSelectNone={handleSelectNone}
                onSelectAll={handleSelectAll}
                type="assigned"
                positions={formData.positions}
                onPositionChange={handlePositionChangeStable}
                onRateChange={handleRateChangeStable}
                theme={theme}
                loading={loading} // pass loading
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Shift Name
                <Tooltip title="Enter a unique name for this shift" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                </Tooltip>
              </Typography>
              <CustomTextField
                fullWidth
                value={formData.shiftName}
                onChange={(e) => {
                  updateFormField("shiftName", e.target.value);
                  clearFieldError("shiftName");
                }}
                showError={!!errors.shiftName}
                errorMessage={errors.shiftName}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 48,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Shift Start Date
                <Tooltip title="The date when this shift becomes active" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                </Tooltip>
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={formData.shiftStartDate}
                  onChange={(date) => {
                    updateFormField("shiftStartDate", date);
                    clearFieldError("shiftStartDate");
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      fullWidth
                      showError={!!errors.shiftStartDate}
                      errorMessage={errors.shiftStartDate}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 48,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Shift End Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={formData.shiftEndDate}
                  onChange={(date) => {
                    updateFormField("shiftEndDate", date);
                    clearFieldError("shiftEndDate");
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      fullWidth
                      showError={!!errors.shiftEndDate}
                      errorMessage={errors.shiftEndDate}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 48,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Default Pay Rate
                <Tooltip title="The default hourly pay rate for employees on this shift" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                </Tooltip>
              </Typography>
              <CustomTextField
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: "$",
                  inputProps: {
                    min: 0,
                    step: "0.01",
                    inputMode: "decimal",
                  },
                }}
                value={formData.payRate}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                    updateFormField("payRate", value);
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 48,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                Default Bill Rate
                <Tooltip
                  title="The default hourly billing rate charged to clients for this shift"
                  arrow
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                </Tooltip>
              </Typography>
              <CustomTextField
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: "$",
                  inputProps: {
                    min: 0,
                    step: "0.01",
                    inputMode: "decimal",
                  },
                }}
                value={formData.billRate}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                    updateFormField("billRate", value);
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 48,
                  },
                }}
              />
            </Grid>
            <Grid item xs={4} sx={{ display: "flex", alignItems: "end" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleApplyRatesToAll}
                disabled={
                  formData.assignedStaff.length === 0 || (!formData.payRate && !formData.billRate)
                }
                sx={{
                  height: 42,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                APPLY TO ALL ASSIGNED STAFF
              </Button>
            </Grid>
          </Grid>

          {/* Positions Section */}
          <ShiftPositions
            positions={formData.positions}
            onAddPosition={addNewPosition}
            onPositionHandler={positionHandler}
            onRemovePosition={removePosition}
            assignedStaff={formData.assignedStaff}
          />

          {!formData.shiftName ? null : (
            <WeeklySchedule
              shifts={[formData]}
              onScheduleUpdate={(updatedShifts) => {
                // Additional validation to ensure no incomplete time pairs
                let hasValidSchedules = true;

                if (updatedShifts && updatedShifts[0]) {
                  const shift = updatedShifts[0];
                  const scheduleToCheck = shift.defaultSchedule || shift.schedule || {};

                  // Check if schedule exists and validate time pairs
                  if (Object.keys(scheduleToCheck).length > 0) {
                    const hasIncompleteTimePairs = Object.entries(scheduleToCheck).some(
                      ([day, times]) => {
                        if (!times) return true;

                        // Check for cases where start exists but not end, or end exists but not start
                        const hasStart = times.start && times.start.trim() !== "";
                        const hasEnd = times.end && times.end.trim() !== "";

                        return (hasStart && !hasEnd) || (!hasStart && hasEnd);
                      }
                    );

                    hasValidSchedules = !hasIncompleteTimePairs;
                  }
                }

                // Combine WeeklySchedule validation with our additional validation
                const finalIsValid = hasValidSchedules;
                setIsValidScheduleShifts(finalIsValid);

                if (updatedShifts && updatedShifts[0]) {
                  setFormData((prev) => ({
                    ...prev,
                    defaultSchedule: updatedShifts[0].defaultSchedule || {},
                    schedule: updatedShifts[0].schedule || {},
                  }));
                }
              }}
              isNoSave
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              minWidth: 100,
              textTransform: "uppercase",
            }}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValidScheduleShifts || !isFormValid}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              minWidth: 100,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            SAVE
            <LockIcon sx={{ fontSize: 16 }} />
          </Button>
        </Box>
      </DialogContent>

      <ShiftPositionModal
        setValue={(field, value, options) => {
          if (field === "positions") {
            setFormData((prev) => {
              // If we're editing an existing position, update staff assignments too
              if (positionIndex !== null && currentPosition) {
                const oldPositionName = currentPosition.positionName;
                const newPositionName = value[positionIndex]?.positionName;

                // Update staff assignments if position name changed
                const updatedAssignedStaff = prev.assignedStaff.map((staff) => ({
                  ...staff,
                  assignedPosition:
                    staff.assignedPosition === oldPositionName
                      ? newPositionName
                      : staff.assignedPosition,
                }));

                return {
                  ...prev,
                  positions: value,
                  assignedStaff: updatedAssignedStaff,
                };
              }

              return { ...prev, positions: value };
            });
          }
        }}
        getValues={(field) => {
          if (field === "positions") {
            return formData.positions;
          }
          return formData[field];
        }}
        shiftPositionModalOpen={shiftPositionModalOpen}
        setShiftPositionModalOpen={setShiftPositionModalOpen}
        currentPosition={currentPosition}
        setCurrentPosition={setCurrentPosition}
        positionIndex={positionIndex}
        setPositionIndex={setPositionIndex}
        assignedStaff={formData.assignedStaff}
      />
    </Dialog>
  );
};

export default ShiftSettingsModal;
